import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import {
  loadAgencyLinkObjects, loadAgencyThreads, loadChannels, loadCreatorLinkObjects, loadCreatorThread, loadDoneNotifications, loadMembers,
  loadMentionable, loadOpenNotifications, loadStaffNotificationContext, loadThread, loadUnread, type NotificationScope, type UnreadScope,
} from "./data";
import type { Portal } from "./model";

/**
 * Abfragen der Gruppe „Nachrichten“. Eigene Schlüssel enden auf „nachrichten“, damit sie den Cache anderer Gruppen
 * nicht mit anderen Spalten füllen. Nach jeder schreibenden Aktion lädt die App ohnehin alle Abfragen neu.
 *
 * Echtzeit (0024) gibt es nur auf Supabase, und die Datenschnittstelle der App kennt noch kein Abonnement. Bis dahin
 * laden Gesprächsliste, offener Verlauf, Glocke und Zähler mit Supabase alle 30 Sekunden nach, und nur bei sichtbarem
 * Tab (wie der Notnagel des Altportals, AD-186/TA-117). In der Demo schreibt niemand außer der angemeldeten Person;
 * dort genügt das Neuladen nach jeder eigenen Aktion, das die App ohnehin auslöst.
 */
const K = "nachrichten";
export const POLL_MS = 30_000;
const poll = () => (api.mode === "demo" ? false : POLL_MS);

export const qk = {
  agencyThreads: (orgId: string) => ["my_chat_threads", orgId, "agentur", K] as const,
  creatorThread: (orgId: string, talentId: string) => ["my_chat_threads", orgId, talentId, K] as const,
  thread: (orgId: string, talentId: string, limit: number) => ["messages", orgId, talentId, limit, K] as const,
  members: (orgId: string) => ["memberships", orgId, K] as const,
  links: (portal: Portal, orgId: string, talentId: string) => ["chat_links", portal, orgId, talentId, K] as const,
  open: (s: NotificationScope) => ["my_notifications", s.orgId, s.audience, s.talentId ?? null, K] as const,
  done: (s: NotificationScope, userId: string | null) => ["notifications", s.orgId, s.audience, s.talentId ?? null, "erledigt", userId, K] as const,
  staffContext: (orgId: string, invoiceIds: readonly string[]) => ["notification_context", orgId, [...invoiceIds].sort().join(","), K] as const,
  channels: (portal: Portal, orgId: string, talentId: string | null, userId: string | null) => ["channels", portal, orgId, talentId, userId, K] as const,
  unread: (s: UnreadScope) => ["unread", s.portal, s.orgId, s.talentId ?? null, K] as const,
};

export const useAgencyThreads = (orgId: string) =>
  useQuery({ queryKey: qk.agencyThreads(orgId), queryFn: () => loadAgencyThreads(api, orgId), refetchInterval: poll() });

export const useCreatorThread = (orgId: string, talentId: string) =>
  useQuery({ queryKey: qk.creatorThread(orgId, talentId), queryFn: () => loadCreatorThread(api, orgId, talentId), refetchInterval: poll() });

/**
 * Verlauf eines Fadens; beim Nachladen älterer Nachrichten bleibt der bisherige Stand sichtbar (kein Springen). Im Agentur-Portal
 * mit den internen Notizen des Teams (0393); der Schlüssel bleibt gleich, damit „gelesen“ und Senden denselben Cache treffen.
 */
export const useThread = (orgId: string, talentId: string, limit: number, portal: Portal = "agentur") =>
  useQuery({
    queryKey: qk.thread(orgId, talentId, limit),
    queryFn: () => loadThread(api, orgId, talentId, limit, { notes: portal === "agentur" }),
    refetchInterval: poll(),
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === talentId ? prev : undefined),
  });

/** Wen das Team in einer internen Notiz erwähnen kann (0393, nur Agentur). */
export const useMentionable = (talentId: string, enabled: boolean) =>
  useQuery({ queryKey: ["chat_mentionable", talentId, K], enabled, queryFn: () => loadMentionable(api, talentId), staleTime: 5 * 60_000 });

export const useMembers = (orgId: string, enabled = true) =>
  useQuery({ queryKey: qk.members(orgId), enabled, queryFn: () => loadMembers(api, orgId), staleTime: 5 * 60_000 });

/** Creator: Anzeigenamen der Team-Personen, die geschrieben haben (Sicht talent_chat_senders, 0034). */
export const useChatSenders = (orgId: string, talentId: string) =>
  useQuery({ queryKey: ["chat_senders", orgId, talentId], queryFn: () => api.select<{ user_id: string; display_name: string }>("talent_chat_senders", { columns: "user_id, display_name", filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId]] }) });

export const useLinkObjects = (portal: Portal, orgId: string, talentId: string) =>
  useQuery({
    queryKey: qk.links(portal, orgId, talentId),
    queryFn: () => (portal === "agentur" ? loadAgencyLinkObjects(api, orgId, talentId) : loadCreatorLinkObjects(api, orgId, talentId)),
  });

export const useOpenNotifications = (s: NotificationScope) =>
  useQuery({ queryKey: qk.open(s), queryFn: () => loadOpenNotifications(api, s), refetchInterval: poll() });

/** Erledigtes erst laden, wenn der Filter „Erledigt“ gewählt ist. */
export const useDoneNotifications = (s: NotificationScope, userId: string | null, enabled: boolean) =>
  useQuery({ queryKey: qk.done(s, userId), enabled: enabled && !!userId, queryFn: () => loadDoneNotifications(api, s, userId!) });

/** Beim Wechsel auf „Erledigt“ kommen Rechnungen dazu: bis sie geladen sind, bleiben die bisherigen Namen stehen. */
export const useStaffNotificationContext = (orgId: string, invoiceIds: readonly string[], enabled: boolean) =>
  useQuery({
    queryKey: qk.staffContext(orgId, invoiceIds),
    enabled,
    queryFn: () => loadStaffNotificationContext(api, orgId, invoiceIds),
    placeholderData: (prev) => prev,
  });

export const useChannels = (portal: Portal, orgId: string, talentId: string | null, userId: string | null) =>
  useQuery({ queryKey: qk.channels(portal, orgId, talentId, userId), queryFn: () => loadChannels(api, { portal, orgId, talentId, userId }) });

export const useUnreadData = (s: UnreadScope | null) =>
  useQuery({
    queryKey: s ? qk.unread(s) : ["unread", "aus", K],
    enabled: !!s,
    queryFn: () => loadUnread(api, s!),
    refetchInterval: poll(),
  });
